import React from 'react';
import { Calendar, TrendingUp, Percent, AlertCircle, DollarSign, PiggyBank, Shield } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета инвестиции в криптовалюту
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Получение символа валюты
  const getCurrencySymbol = (currencyType) => {
    switch (currencyType) {
      case 'rub': return '₽';
      case 'usd': return '$';
      case 'eur': return '€';
      case 'cny': return '¥';
      default: return '₽';
    }
  };

  const currencySymbol = getCurrencySymbol(results.currencyType);

  // Определение цвета доходности в зависимости от значения
  const getReturnColor = (returnValue) => {
    if (returnValue >= 20) return 'green';
    if (returnValue > 0) return 'emerald';
    if (returnValue === 0) return 'gray';
    return 'red';
  };

  // Определение класса риска
  const getRiskClass = (riskScore) => {
    if (riskScore < 65) return 'low';
    if (riskScore < 80) return 'medium';
    return 'high';
  };

  const riskClass = getRiskClass(results.riskScore);
  const riskColors = {
    low: 'green',
    medium: 'orange',
    high: 'red'
  };

  // Функция для форматирования чисел с валютой
  const formatNumberWithCurrency = (value, decimals = 0) => {
    return `${formatNumber(value, decimals)} ${currencySymbol}`;
  };

  return (
    <ToolResult title="Результаты расчета инвестиции">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Итоговая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <PiggyBank className="w-4 h-4"/>
            Итоговая стоимость инвестиции
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatNumberWithCurrency(results.finalInvestment)}
            </span>
            <span className={`text-sm font-medium text-${getReturnColor(results.returnPercentage)}-600 dark:text-${getReturnColor(results.returnPercentage)}-400`}>
              ({formatNumber(results.returnPercentage, 1)}%)
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Через {results.investmentPeriodMonths} {pluralize(results.investmentPeriodMonths, 'месяц', 'месяца', 'месяцев')}
          </p>
        </div>

        {/* Годовая доходность */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Годовая доходность
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold text-${getReturnColor(results.annualizedReturnPercentage)}-600 dark:text-${getReturnColor(results.annualizedReturnPercentage)}-400`} aria-live="polite">
              {formatNumber(results.annualizedReturnPercentage, 1)}%
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              (В рублях)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Эквивалент ставки {formatNumber(results.annualizedReturnPercentage / 12, 1)}% в месяц
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Первоначальная инвестиция" 
          value={formatNumberWithCurrency(results.initialInvestment)} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Общая сумма инвестиций" 
          value={formatNumberWithCurrency(results.totalInvestment)} 
          color={results.totalInvestment > results.initialInvestment ? "blue" : "indigo"}
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Общая прибыль" 
          value={formatNumberWithCurrency(results.totalProfit)} 
          color={getReturnColor(results.returnPercentage)}
          icon={<TrendingUp className="w-4 h-4"/>}
        />
        
        {results.totalFees > 0 && (
          <ResultItem 
            label="Всего комиссий" 
            value={formatNumberWithCurrency(results.totalFees)} 
            color="red" 
          />
        )}
        
        {results.totalTax > 0 && (
          <ResultItem 
            label="Сумма налога (НДФЛ)" 
            value={formatNumberWithCurrency(results.totalTax)} 
            color="red" 
          />
        )}
        
        <ResultItem 
          label="Срок инвестиции" 
          value={`${results.investmentPeriodMonths} ${pluralize(results.investmentPeriodMonths, 'месяц', 'месяца', 'месяцев')} (${Math.floor(results.investmentPeriodMonths / 12)} ${pluralize(Math.floor(results.investmentPeriodMonths / 12), 'год', 'года', 'лет')} и ${results.investmentPeriodMonths % 12} ${pluralize(results.investmentPeriodMonths % 12, 'месяц', 'месяца', 'месяцев')})`} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Среднемесячный рост" 
          value={`${formatNumber(results.monthlyGrowthRate, 1)}%`} 
          color={results.monthlyGrowthRate > 0 ? "green" : "red"}
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Уровень риска" 
          value={`${formatNumber(results.riskScore, 0)}/100 (${riskClass === 'low' ? 'Низкий' : riskClass === 'medium' ? 'Средний' : 'Высокий'})`} 
          color={riskColors[riskClass]} 
          icon={<Shield className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Скорректированная доходность" 
          value={`${formatNumber(results.riskAdjustedReturn, 1)}%`} 
          color={results.riskAdjustedReturn > 0 ? "emerald" : "red"}
          tooltip="Годовая доходность, скорректированная с учетом риска"
        />

        {results.hasScenariosAnalysis && (
          <>
            <ResultItem 
              label="Пессимистичный сценарий" 
              value={formatNumberWithCurrency(results.pessimisticFinalValue)} 
              color="red" 
            />
            
            <ResultItem 
              label="Оптимистичный сценарий" 
              value={formatNumberWithCurrency(results.optimisticFinalValue)} 
              color="green" 
            />
          </>
        )}
      </div>

      {/* Правовая информация */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важная правовая информация</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Данный расчет носит ознакомительный характер и не является инвестиционной рекомендацией. 
              Криптовалютные инвестиции сопряжены с высоким риском. Стоимость активов может значительно колебаться 
              и не гарантирует получение дохода. В России операции с криптовалютой регулируются Федеральным законом 
              "О цифровых финансовых активах" от 31.07.2020 N 259-ФЗ.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ResultsOverview;